import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "international_academic_contribution";

const InternationalAcademicContribution = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    starting_date: null,
    ending_date: null,
    nature: "",
    institution_name: "",
    institution_accreditation: "",
    qs_ranking: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

 const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = Array.isArray(data)
      ? data
          .map((rec) => ({
            ...rec,
            starting_date: rec.starting_date
              ? new Date(rec.starting_date)
              : null,
            ending_date: rec.ending_date
              ? new Date(rec.ending_date)
              : null,
          }))
          .sort((a, b) => {
            if (!a.starting_date) return 1; // Empty dates at the bottom
            if (!b.starting_date) return -1;
            return b.starting_date - a.starting_date; // Latest first
          })
      : [];

    setRecords(processed);
  } catch (err) {
    toast.error(err.message);
  }
};
  useEffect(() => {
    fetchRecords();
  }, []);

  const convertToSQLDate = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const m = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    return `${y}-${m}-01`;
  };

  const formatDate = (date) => (date ? format(new Date(date), "MM/yyyy") : "");

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      starting_date: convertToSQLDate(formData.starting_date),
      ending_date: convertToSQLDate(formData.ending_date),
    };
    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");
      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );
      setFormData({
        starting_date: null,
        ending_date: null,
        nature: "",
        institution_name: "",
        institution_accreditation: "",
        qs_ranking: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      starting_date: rec.starting_date ? new Date(rec.starting_date) : null,
      ending_date: rec.ending_date ? new Date(rec.ending_date) : null,
      nature: rec.nature || "",
      institution_name: rec.institution_name || "",
      institution_accreditation: rec.institution_accreditation || "",
      qs_ranking: rec.qs_ranking || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          International Academic Affiliation/Contribution
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>From (mm/yyyy)</label>
              <DatePicker
                selected={formData.starting_date}
                onChange={(d) => setFormData({ ...formData, starting_date: d })}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                placeholderText="From"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>To (mm/yyyy)</label>
              <DatePicker
                selected={formData.ending_date}
                onChange={(d) => setFormData({ ...formData, ending_date: d })}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                placeholderText="To"
                style={inputStyle}
              />
            </div>
          </div>

          {[
            { label: "Nature of the Affiliation", name: "nature" },
            { label: "Institution Name", name: "institution_name" },
            { label: "Accreditation", name: "institution_accreditation" },
            { label: "QS Ranking", name: "qs_ranking" },
            { label: "Country", name: "country" },
          ].map((field) => (
            <div key={field.name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {field.label}
              </label>
              <input
                type="text"
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>
          ))}

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "From",
                    "To",
                    "Nature",
                    "Institution",
                    "Accreditation",
                    "QS Ranking",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.starting_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.ending_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.nature}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution_accreditation}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.qs_ranking}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default InternationalAcademicContribution;
